"use client"

import Image from "next/image"
import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import { BookingCta } from "@/components/ui/booking-cta"
import { InsuranceCoverageCta } from "@/components/marketing/insurance-coverage-cta"
import { ArrowRightIcon, buttonVariants } from "@/components/ui/button"
import { NewMembershipPill } from "@/components/ui/new-membership-pill"
import { homeKeys as k } from "@/i18n/home-keys"
import { blobMediaUrl } from "@/lib/blob-url"
import { cn } from "@/lib/utils"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { useCountry } from "@/components/layout/country-provider"
import { isAllowedInCountry } from "@/lib/country-gates"

// Order: biomarkers + blood-tests lead (headline value), action plan +
// support (human touch), then member pricing + upload (practical). The
// `*Feature{N}` keys remain semantic (icon-label correspondence is by
// index here, not by key name).
const FEATURE_IMAGES = [
  blobMediaUrl("/images/homepage-membership/biomarkers.png"),
  blobMediaUrl("/images/homepage-membership/blood-test-arm.png"),
  blobMediaUrl("/images/homepage-membership/action-plan.png"),
  blobMediaUrl("/images/homepage-membership/support-chat.png"),
  blobMediaUrl("/images/homepage-membership/member-pricing-card-v2.png"),
  blobMediaUrl("/images/homepage-membership/upload-vial.png"),
] as const

const FEATURE_KEYS = [
  k.homepageMembershipFeature1, // 80+ biomarkers
  k.homepageMembershipFeature5, // 1 to 4 blood tests / year, depending on plan
  k.homepageMembershipFeature3, // Action plan
  k.homepageMembershipFeature2, // Prevention support team
  k.homepageMembershipFeature4, // Member pricing
  k.homepageMembershipFeature6, // Upload existing results
] as const

interface HomepageMembershipSectionProps {
  /** Override the section's vertical padding. Defaults to homepage spacing. */
  paddingClass?: string
  /** Full-bleed variant: the background image spans the full viewport width
   *  (no rounded card, no horizontal max-width), while the content keeps its
   *  1440-centered layout. Used as the closing section on /go/bloodtest. */
  fullBleed?: boolean
  /** Secondary CTA next to the booking button. "learn-more" (default) links to
   *  /membership; "insurance" shows the CH-only insurance-coverage CTA instead
   *  (used on /go/membership). */
  secondaryCta?: "learn-more" | "insurance"
  /**
   * Override the third heading line, which defaults to
   * `home.homepage_membership_heading_3` ("Verified by Swiss doctors."). Set
   * by surfaces that must not make a Swiss-clinician claim — the German Munich
   * LP passes a nationality-free line. Leave unset everywhere else so the
   * shared string stays the single source of truth.
   */
  headingLine3?: string
}

export function HomepageMembershipSection({
  paddingClass = "py-16 md:py-24",
  fullBleed = false,
  secondaryCta = "learn-more",
  headingLine3,
}: HomepageMembershipSectionProps = {}) {
  const t = useTranslations("home")
  const ut = useTranslations("ui")
  const navT = useTranslations("nav")
  const lp = useLocalePath()
  const { code: country } = useCountry()
  // This band renders on the homepage, /pricing, /how-it-works and /go/checkup.
  // In the membership-first markets (CH + AT) its CTA leads with "Book check-up"
  // — the concluded winner of the main-CTA copy experiment (see
  // lib/experiments.ts), matching the navbar so a visitor never sees two
  // different asks on one page. NL/DE keep the testing-first fallback.
  const isMembershipFirst = isAllowedInCountry(
    "feature:membership-first-positioning",
    country
  )

  return (
    <section className={`lazy-section ${fullBleed ? "" : paddingClass}`}>
      <div
        className={
          fullBleed ? "w-full" : "mx-auto w-full max-w-[1440px] px-4 md:px-10"
        }
      >
        <div
          className={cn(
            "relative overflow-hidden",
            !fullBleed && "rounded-[24px] md:rounded-[32px]"
          )}
        >
          {/* Premium lifestyle background — photo blends into a warm cream
              gradient on the right where the content panel sits. The
              previous `unoptimized` flag was bypassing Vercel's image
              optimizer, which Lighthouse flagged as ~240 KB of WebP/AVIF
              savings. The optimizer preserves the cream-gradient transition
              that the content panel sits over, so removing it is safe.
              Loading is intentionally lazy (no `priority`): this section is
              well below the fold, so preloading it competed for bandwidth
              with the actual LCP hero on slow 4G — PSI flagged this as a
              third high-priority preload alongside hero.jpeg /
              hero-mobile.jpeg. Browser fetches it as the user scrolls. */}
          {/* Full-bleed closing block uses a dedicated sunset image (man on
              the left, sky on the right under the copy); the homepage variant
              keeps the original premium photo. Transform nudges the framing
              so his face clears the heading. */}
          <Image
            src={
              fullBleed
                ? blobMediaUrl("/images/homepage-membership/bg-membership.jpg")
                : blobMediaUrl("/images/homepage-membership/bg-premium.jpg")
            }
            alt=""
            aria-hidden="true"
            fill
            sizes={fullBleed ? "100vw" : "(min-width: 1440px) 1360px, 100vw"}
            className="object-cover"
            style={
              fullBleed
                ? { transform: "scale(1.15) translate(1%, -2%)" }
                : undefined
            }
          />

          {/* Mobile + tablet readability wash — horizontal gradient stronger
              on the right (content panel) tapering to lighter on the left so
              the photo still breathes. 3px backdrop-blur softens underlying
              detail behind the heading without measurable perf cost.
              Hidden on lg+: the photo's built-in cream gradient handles it. */}
          <div
            aria-hidden="true"
            className="absolute inset-0 z-[1] bg-gradient-to-l from-white/65 via-white/45 to-white/20 backdrop-blur-[3px] lg:hidden"
          />

          <div
            className={cn(
              "relative z-10 px-6 py-12 md:px-10 md:py-16 lg:py-20",
              fullBleed && "mx-auto max-w-[1440px]"
            )}
          >
            <div
              className={cn(
                fullBleed &&
                  "grid grid-cols-1 gap-8 md:grid-cols-[1fr_2fr] md:gap-12"
              )}
            >
              {/* Full-bleed (closing block on /go/*) clears the man's face with
                  an empty left third; the homepage variant uses bg-premium.jpg,
                  whose subject sits more centre-left, so it right-aligns the
                  panel instead (ml-auto below) to clear the face. */}
              {fullBleed && <div aria-hidden className="hidden md:block" />}
              <div
                className={cn(
                  "w-full max-w-[608px] min-w-0",
                  !fullBleed && "ml-auto xl:mr-[3%]"
                )}
              >
                <NewMembershipPill
                  label={t(k.homepageMembershipPill)}
                  noMagicStroke
                />

                {/* Heading — 3 line beats per Figma */}
                <h2 className="type-display-sm mt-6 text-neutral-900">
                  {t(k.homepageMembershipHeading1)}
                  <br />
                  {t(k.homepageMembershipHeading2)}
                  <br />
                  {headingLine3 ?? t(k.homepageMembershipHeading3)}
                </h2>

                {/* Price */}
                <p className="type-label-lg mt-6 text-neutral-900 normal-case">
                  {t(k.homepageMembershipPrice)}
                </p>

                {/* CTAs */}
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <BookingCta
                    forMembership
                    className={cn(
                      buttonVariants({ variant: "primary" }),
                      "group"
                    )}
                    trackingLabel="homepage-membership-cta"
                    trackingLocation="homepage"
                  >
                    {isMembershipFirst
                      ? ut("mainCtaStartCheckup")
                      : t(k.homepageMembershipCtaPrimary)}
                    <ArrowRightIcon className="ml-2 transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                  </BookingCta>
                  {secondaryCta === "insurance" ? (
                    /* hover:bg-transparent drops the md variant's link-wide
                       hover fill — keeps only the underline + heart pulse. */
                    <InsuranceCoverageCta
                      size="md"
                      icon="heart-check-pulse"
                      className="hover:bg-transparent"
                    />
                  ) : (
                    <Link
                      href={lp("/membership")}
                      // The visible label is a bare "Learn more" — Lighthouse's
                      // `link-text` audit reads the link's text content (not the
                      // accessible name), so an aria-label alone doesn't satisfy
                      // it and /pricing's SEO score stays at 89. The sr-only
                      // suffix below names the destination in the text content
                      // (fixing the audit) while the aria-label keeps the
                      // screen-reader name clean. Composed from two keys that
                      // already exist in every locale rather than adding a new
                      // string.
                      aria-label={`${t(k.homepageMembershipCtaSecondary)}: ${navT("membership")}`}
                      className={cn(
                        buttonVariants({ variant: "outline" }),
                        "group relative overflow-hidden !px-10"
                      )}
                    >
                      <span className="inline-block transition-transform duration-200 ease-out group-hover:-translate-x-2">
                        {t(k.homepageMembershipCtaSecondary)}
                      </span>
                      <span className="sr-only">{`: ${navT("membership")}`}</span>
                      <span className="absolute right-4 translate-x-3 opacity-0 transition-all duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100">
                        <ArrowRightIcon />
                      </span>
                    </Link>
                  )}
                </div>

                {/* 6-card feature grid (3 rows × 2 columns on md+).
                  `max-w-[480px]` narrower than the heading column so more
                  labels wrap to two lines, giving the block a tighter feel. */}
                <ul className="mt-10 grid max-w-[480px] grid-cols-1 gap-x-2 gap-y-1 md:mt-12 md:grid-cols-2">
                  {FEATURE_KEYS.map((featureKey, idx) => (
                    <li
                      key={featureKey}
                      className="flex items-center gap-3 rounded-[16px] py-2 pr-3"
                    >
                      <div className="relative size-10 shrink-0 overflow-hidden rounded-full">
                        <Image
                          src={FEATURE_IMAGES[idx]}
                          alt=""
                          fill
                          sizes="40px"
                          className="object-cover"
                        />
                      </div>
                      <span className="type-title-sm leading-tight text-neutral-900">
                        {t(featureKey)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
